import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { 
  ArrowLeft, Save, Eye, Calendar, Plus, Trash2, 
  Sparkles, Wand2, Loader2, Image, Link, X,
  ChevronDown, ChevronUp, GripVertical
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogEditor = ({ blog, cmsPin, onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [authors, setAuthors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [activeSection, setActiveSection] = useState('basic');
  
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    meta_title: '',
    meta_description: '',
    excerpt: '',
    keywords: [],
    introduction: '',
    key_insights: ['', '', ''],
    content: '',
    faq_section: [{ question: '', answer: '' }],
    expert_takeaway: '',
    conclusion: '',
    cta_text: "Need help with digital marketing for your business? Buzzcraft Maven helps brands scale through performance marketing and strategic branding.",
    cta_link: '/contact',
    featured_image: '',
    category: 'digital-marketing',
    tags: [],
    cluster_id: '',
    author_id: '',
    target_locations: [],
    read_time: 5,
    status: 'draft',
    is_featured: false,
    is_pillar: false,
    internal_links: [],
    related_service_slugs: [],
    scheduled_at: null
  });

  const [keywordInput, setKeywordInput] = useState('');
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    fetchMetadata();
    if (blog) {
      setFormData({
        ...blog,
        keywords: blog.keywords || [],
        key_insights: blog.key_insights?.length ? blog.key_insights : ['', '', ''],
        faq_section: blog.faq_section?.length ? blog.faq_section : [{ question: '', answer: '' }],
        tags: blog.tags || [],
        target_locations: blog.target_locations || [],
        internal_links: blog.internal_links || [],
        related_service_slugs: blog.related_service_slugs || []
      });
    }
  }, [blog]);

  const fetchMetadata = async () => {
    try {
      const [authorsRes, categoriesRes, clustersRes] = await Promise.all([
        fetch(`${API_URL}/api/cms/authors`, { headers: { 'X-CMS-Pin': cmsPin } }),
        fetch(`${API_URL}/api/cms/categories`, { headers: { 'X-CMS-Pin': cmsPin } }),
        fetch(`${API_URL}/api/cms/clusters`, { headers: { 'X-CMS-Pin': cmsPin } })
      ]);
      
      const [authorsData, categoriesData, clustersData] = await Promise.all([
        authorsRes.json(),
        categoriesRes.json(),
        clustersRes.json()
      ]);
      
      setAuthors(authorsData);
      setCategories(categoriesData);
      setClusters(clustersData);
      
      // Set default author if not set
      if (!formData.author_id && authorsData.length > 0) {
        setFormData(prev => ({ ...prev, author_id: authorsData[0].id }));
      }
    } catch (error) {
      console.error('Failed to fetch metadata:', error);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData(prev => ({
      ...prev,
      title,
      slug: blog ? prev.slug : generateSlug(title),
      meta_title: prev.meta_title || title
    }));
  };

  const addKeyword = () => {
    if (keywordInput.trim() && !formData.keywords.includes(keywordInput.trim())) {
      setFormData(prev => ({
        ...prev,
        keywords: [...prev.keywords, keywordInput.trim()]
      }));
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword) => {
    setFormData(prev => ({
      ...prev,
      keywords: prev.keywords.filter(k => k !== keyword)
    }));
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const updateKeyInsight = (index, value) => {
    const newInsights = [...formData.key_insights];
    newInsights[index] = value;
    setFormData(prev => ({ ...prev, key_insights: newInsights }));
  };

  const addKeyInsight = () => {
    setFormData(prev => ({
      ...prev,
      key_insights: [...prev.key_insights, '']
    }));
  };

  const removeKeyInsight = (index) => {
    if (formData.key_insights.length > 1) {
      const newInsights = formData.key_insights.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, key_insights: newInsights }));
    }
  };

  const updateFAQ = (index, field, value) => {
    const newFAQs = [...formData.faq_section];
    newFAQs[index] = { ...newFAQs[index], [field]: value };
    setFormData(prev => ({ ...prev, faq_section: newFAQs }));
  };

  const addFAQ = () => {
    setFormData(prev => ({
      ...prev,
      faq_section: [...prev.faq_section, { question: '', answer: '' }]
    }));
  };

  const removeFAQ = (index) => {
    if (formData.faq_section.length > 1) {
      const newFAQs = formData.faq_section.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, faq_section: newFAQs }));
    }
  };

  const handleSubmit = async (status = 'draft') => {
    if (!formData.title || !formData.slug || !formData.content) {
      toast.error('Please fill in required fields: Title, Slug, and Content');
      return;
    }

    setSaving(true);
    
    const payload = {
      ...formData,
      status,
      key_insights: formData.key_insights.filter(i => i.trim()),
      faq_section: formData.faq_section.filter(f => f.question.trim() && f.answer.trim())
    };

    try {
      const url = blog 
        ? `${API_URL}/api/cms/blogs/${blog.id}`
        : `${API_URL}/api/cms/blogs`;
      
      const response = await fetch(url, {
        method: blog ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CMS-Pin': cmsPin
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(blog ? 'Blog updated!' : 'Blog created!');
        onSave();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save blog');
      }
    } catch (error) {
      toast.error('Failed to save blog');
    } finally {
      setSaving(false);
    }
  };

  const generateAIContent = async () => {
    if (!formData.title) {
      toast.error('Please enter a title first');
      return;
    }

    setGenerating(true);
    
    try {
      const response = await fetch(`${API_URL}/api/cms/ai/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CMS-Pin': cmsPin
        },
        body: JSON.stringify({
          topic: formData.title,
          keyword: formData.keywords[0] || formData.title,
          target_audience: 'business owners and marketers',
          content_type: formData.is_pillar ? 'pillar' : 'blog'
        })
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({
          ...prev,
          meta_description: data.meta_description || prev.meta_description,
          key_insights: data.key_insights?.length ? data.key_insights : prev.key_insights,
          faq_section: data.faq_suggestions?.length ? data.faq_suggestions : prev.faq_section,
          keywords: data.keywords?.length ? [...new Set([...prev.keywords, ...data.keywords])] : prev.keywords
        }));
        toast.success('AI content generated!');
      } else {
        toast.error('AI generation not available');
      }
    } catch (error) {
      toast.error('AI generation failed. You can manually fill in the content.');
    } finally {
      setGenerating(false);
    }
  };

  const sections = [
    { id: 'basic', label: 'Basic Info' },
    { id: 'seo', label: 'SEO & Meta' },
    { id: 'content', label: 'Content' },
    { id: 'structure', label: 'AI Structure' },
    { id: 'media', label: 'Media & Links' },
    { id: 'settings', label: 'Settings' }
  ];

  return (
    <div className="fixed inset-0 bg-slate-900 z-50 overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-slate-800/95 backdrop-blur border-b border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <div className="flex items-center gap-4">
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700 transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-xl font-bold text-white">
                {blog ? 'Edit Blog Post' : 'Create New Blog Post'}
              </h1>
              <p className="text-sm text-slate-400">
                {formData.slug ? `/blog/${formData.slug}` : 'Enter a title to generate slug'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={generateAIContent}
              disabled={generating}
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
            >
              {generating ? <Loader2 size={18} className="animate-spin" /> : <Wand2 size={18} />}
              AI Assist
            </button>
            <button
              onClick={() => handleSubmit('draft')}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors disabled:opacity-50"
            >
              <Save size={18} />
              Save Draft
            </button>
            <button
              onClick={() => handleSubmit('published')}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 size={18} className="animate-spin" /> : <Eye size={18} />}
              Publish
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex gap-8">
          {/* Section Navigation */}
          <div className="w-48 flex-shrink-0">
            <nav className="sticky top-24 space-y-1">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${
                    activeSection === section.id
                      ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                      : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  {section.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Form Content */}
          <div className="flex-1 space-y-8">
            {/* Basic Info */}
            {activeSection === 'basic' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                <h2 className="text-lg font-semibold text-white border-b border-slate-700 pb-2">Basic Information</h2>
                
                <div>
                  <label className="block text-slate-300 text-sm mb-2">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={handleTitleChange}
                    placeholder="Enter blog title"
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-orange-500"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 text-sm mb-2">URL Slug *</label>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500">/blog/</span>
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={(e) => setFormData(prev => ({ ...prev, slug: generateSlug(e.target.value) }))}
                      placeholder="url-friendly-slug"
                      className="flex-1 px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-orange-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-slate-300 text-sm mb-2">Excerpt</label>
                  <textarea
                    value={formData.excerpt}
                    onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
                    placeholder="Brief summary for blog cards and social sharing"
                    rows={2}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-orange-500 resize-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-300 text-sm mb-2">Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-orange-500"
                    >
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.slug}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-300 text-sm mb-2">Author</label>
                    <select
                      value={formData.author_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, author_id: e.target.value }))}
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-orange-500"
                    >
                      {authors.map((author) => (
                        <option key={author.id} value={author.id}>{author.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-slate-300 text-sm mb-2">Tags</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.tags.map((tag, i) => (
                      <span key={i} className="flex items-center gap-1 px-3 py-1 bg-slate-700 text-slate-300 rounded-full text-sm">
                        {tag}
                        <button onClick={() => removeTag(tag)} className="hover:text-red-400">
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                      placeholder="Add tag"
                      className="flex-1 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-orange-500"
                    />
                    <button onClick={addTag} className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600">
                      <Plus size={18} />
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* SEO & Meta */}
            {activeSection === 'seo' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                <h2 className="text-lg font-semibold text-white border-b border-slate-700 pb-2">SEO & Meta Tags</h2>
                
                <div>
                  <label className="block text-slate-300 text-sm mb-2">Meta Title</label>
                  <input
                    type="text"
                    value={formData.meta_title}
                    onChange={(e) => setFormData(prev => ({ ...prev, meta_title: e.target.value }))}
                    placeholder="SEO-optimized title (60 chars max)"
                    maxLength={60}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-orange-500"
                  />
                  <p className="text-xs text-slate-500 mt-1">{formData.meta_title.length}/60 characters</p>
                </div>

                <div>
                  <label className="block text-slate-300 text-sm mb-2">Meta Description</label>
                  <textarea
                    value={formData.meta_description}
                    onChange={(e) => setFormData(prev => ({ ...prev, meta_description: e.target.value }))}
                    placeholder="SEO-optimized description (160 chars max)"
                    maxLength={160}
                    rows={3}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-orange-500 resize-none"
                  />
                  <p className="text-xs text-slate-500 mt-1">{formData.meta_description.length}/160 characters</p>
                </div>

                <div>
                  <label className="block text-slate-300 text-sm mb-2">Target Keywords</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.keywords.map((keyword, i) => (
                      <span key={i} className="flex items-center gap-1 px-3 py-1 bg-orange-500/20 text-orange-400 rounded-full text-sm border border-orange-500/30">
                        {keyword}
                        <button onClick={() => removeKeyword(keyword)} className="hover:text-red-400">
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                      placeholder="Add keyword"
                      className="flex-1 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-orange-500"
                    />
                    <button onClick={addKeyword} className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600">
                      <Plus size={18} />
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Content */}
            {activeSection === 'content' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                <h2 className="text-lg font-semibold text-white border-b border-slate-700 pb-2">Main Content</h2>
                
                <div>
                  <label className="block text-slate-300 text-sm mb-2">Main Content * (Markdown supported)</label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Write your blog content here. Use ## for H2 headings, ### for H3 headings..."
                    rows={20}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-orange-500 resize-y"
                  />
                </div>

                <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                  <h3 className="text-sm font-medium text-slate-300 mb-2">Markdown Quick Reference</h3>
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-400">
                    <span><code className="bg-slate-700 px-1 rounded">## Heading</code> - H2</span>
                    <span><code className="bg-slate-700 px-1 rounded">### Heading</code> - H3</span>
                    <span><code className="bg-slate-700 px-1 rounded">**bold**</code> - Bold</span>
                    <span><code className="bg-slate-700 px-1 rounded">*italic*</code> - Italic</span>
                    <span><code className="bg-slate-700 px-1 rounded">- item</code> - Bullet</span>
                    <span><code className="bg-slate-700 px-1 rounded">[text](url)</code> - Link</span>
                  </div>
                </div>
              </motion.div>
            )}

            {/* AI Structure */}
            {activeSection === 'structure' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                <div className="flex items-center justify-between border-b border-slate-700 pb-2">
                  <h2 className="text-lg font-semibold text-white">AI-Optimized Sections</h2>
                  <button
                    onClick={generateAIContent}
                    disabled={generating}
                    className="flex items-center gap-2 px-3 py-1.5 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700"
                  >
                    {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                    Generate with AI
                  </button>
                </div>

                <div>
                  <label className="block text-slate-300 text-sm mb-2">Introduction</label>
                  <textarea
                    value={formData.introduction}
                    onChange={(e) => setFormData(prev => ({ ...prev, introduction: e.target.value }))}
                    placeholder="Opening paragraph that hooks the reader..."
                    rows={4}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-orange-500 resize-none"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-slate-300 text-sm">Key Insights</label>
                    <button onClick={addKeyInsight} className="text-orange-400 hover:text-orange-300 text-sm flex items-center gap-1">
                      <Plus size={14} /> Add Insight
                    </button>
                  </div>
                  <div className="space-y-2">
                    {formData.key_insights.map((insight, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          type="text"
                          value={insight}
                          onChange={(e) => updateKeyInsight(index, e.target.value)}
                          placeholder={`Key insight ${index + 1}`}
                          className="flex-1 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-orange-500"
                        />
                        {formData.key_insights.length > 1 && (
                          <button onClick={() => removeKeyInsight(index)} className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg">
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-slate-300 text-sm">FAQ Section (for AI Overview)</label>
                    <button onClick={addFAQ} className="text-orange-400 hover:text-orange-300 text-sm flex items-center gap-1">
                      <Plus size={14} /> Add FAQ
                    </button>
                  </div>
                  <div className="space-y-4">
                    {formData.faq_section.map((faq, index) => (
                      <div key={index} className="p-4 bg-slate-800/50 border border-slate-700 rounded-xl">
                        <div className="flex items-start justify-between mb-3">
                          <span className="text-xs text-slate-500">FAQ #{index + 1}</span>
                          {formData.faq_section.length > 1 && (
                            <button onClick={() => removeFAQ(index)} className="text-red-400 hover:text-red-300">
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                        <input
                          type="text"
                          value={faq.question}
                          onChange={(e) => updateFAQ(index, 'question', e.target.value)}
                          placeholder="Question"
                          className="w-full px-4 py-2 mb-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-orange-500"
                        />
                        <textarea
                          value={faq.answer}
                          onChange={(e) => updateFAQ(index, 'answer', e.target.value)}
                          placeholder="Answer"
                          rows={2}
                          className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-orange-500 resize-none"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-slate-300 text-sm mb-2">Expert Takeaway</label>
                  <textarea
                    value={formData.expert_takeaway}
                    onChange={(e) => setFormData(prev => ({ ...prev, expert_takeaway: e.target.value }))}
                    placeholder="Professional insight or actionable advice..."
                    rows={3}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-orange-500 resize-none"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 text-sm mb-2">Conclusion</label>
                  <textarea
                    value={formData.conclusion}
                    onChange={(e) => setFormData(prev => ({ ...prev, conclusion: e.target.value }))}
                    placeholder="Summarize key points and call to action..."
                    rows={3}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-orange-500 resize-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-300 text-sm mb-2">CTA Text</label>
                    <textarea
                      value={formData.cta_text}
                      onChange={(e) => setFormData(prev => ({ ...prev, cta_text: e.target.value }))}
                      rows={2}
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-orange-500 resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-300 text-sm mb-2">CTA Link</label>
                    <input
                      type="text"
                      value={formData.cta_link}
                      onChange={(e) => setFormData(prev => ({ ...prev, cta_link: e.target.value }))}
                      placeholder="/contact"
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-orange-500"
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Media & Links */}
            {activeSection === 'media' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                <h2 className="text-lg font-semibold text-white border-b border-slate-700 pb-2">Media & Internal Links</h2>
                
                <div>
                  <label className="block text-slate-300 text-sm mb-2">Featured Image URL</label>
                  <input
                    type="url"
                    value={formData.featured_image}
                    onChange={(e) => setFormData(prev => ({ ...prev, featured_image: e.target.value }))}
                    placeholder="https://example.com/image.jpg"
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-orange-500"
                  />
                  {formData.featured_image && (
                    <div className="mt-3 p-2 bg-slate-800 rounded-lg">
                      <img src={formData.featured_image} alt="Preview" className="max-h-48 rounded" onError={(e) => e.target.style.display = 'none'} />
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-slate-300 text-sm mb-2">Topic Cluster</label>
                  <select
                    value={formData.cluster_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, cluster_id: e.target.value }))}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-orange-500"
                  >
                    <option value="">No Cluster</option>
                    {clusters.map((cluster) => (
                      <option key={cluster.id} value={cluster.id}>{cluster.name}</option>
                    ))}
                  </select>
                </div>
              </motion.div>
            )}

            {/* Settings */}
            {activeSection === 'settings' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                <h2 className="text-lg font-semibold text-white border-b border-slate-700 pb-2">Blog Settings</h2>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-300 text-sm mb-2">Reading Time (minutes)</label>
                    <input
                      type="number"
                      value={formData.read_time}
                      onChange={(e) => setFormData(prev => ({ ...prev, read_time: parseInt(e.target.value) || 5 }))}
                      min={1}
                      max={60}
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-300 text-sm mb-2">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-orange-500"
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="scheduled">Scheduled</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_featured}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_featured: e.target.checked }))}
                      className="w-5 h-5 rounded bg-slate-700 border-slate-600 text-orange-500 focus:ring-orange-500"
                    />
                    <span className="text-slate-300">Featured Post (shown prominently)</span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_pillar}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_pillar: e.target.checked }))}
                      className="w-5 h-5 rounded bg-slate-700 border-slate-600 text-orange-500 focus:ring-orange-500"
                    />
                    <span className="text-slate-300">Pillar Content (comprehensive guide)</span>
                  </label>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogEditor;
